// src/pages/DashboardPage.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.jpeg';

// ---------- tiny helpers ----------
const slugify = (s = '') =>
  s.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const useDebouncedValue = (value, delay = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

// localStorage helpers
const LS = {
  getJSON: (k, f) => {
    try {
      const raw = localStorage.getItem(k);
      if (!raw) return f ?? [];
      const v = JSON.parse(raw);
      return Array.isArray(v) ? v : (f ?? []);
    } catch {
      return f ?? [];
    }
  },
  setJSON: (k, v) => {
    try { localStorage.setItem(k, JSON.stringify(v)); } catch {}
  },
};

// ---------- Avatar ----------
const Avatar = ({ size = 30 }) => {
  const username = (localStorage.getItem('username') || '').trim();
  const initials = username ? username.replace(/@.*/, '').split(/[._-]/)[0].slice(0, 2).toUpperCase() : '';
  const style = {
    height: size, width: size, borderRadius: 9999, display: 'grid', placeItems: 'center',
    background: 'linear-gradient(135deg, rgba(50,177,255,0.95) 0%, rgba(16,125,250,0.95) 100%)',
    color: '#fff', fontSize: 12, fontWeight: 700, userSelect: 'none',
  };
  return initials ? (
    <div style={style} aria-label="User">{initials}</div>
  ) : (
    <div style={{ ...style }}>
      <svg width={size - 10} height={size - 10} viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="8" r="4" stroke="white" strokeWidth="2" />
        <path d="M4 20c0-4 4-6 8-6s8 2 8 6" stroke="white" strokeWidth="2" />
      </svg>
    </div>
  );
};

// ---------- Tiny Modal ----------
const Modal = ({ open, title, body, confirmLabel = 'Yes', cancelLabel = 'Cancel', onConfirm, onClose }) => {
  if (!open) return null;
  return (
    <div style={modalBack}>
      <div style={modalCard}>
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>{title}</div>
        <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 14 }}>{body}</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnGhost}>{cancelLabel}</button>
          <button onClick={onConfirm} style={btnPrimary}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

// ---------- Card (with customer actions) ----------
const Card = ({ item, onClick, isCustomer, isFav, isClaimed, onAskFavorite, onAskClaim }) => {
  const { name, brand, image } = item || {};
  const [imgLoading, setImgLoading] = useState(!!image);
  const [imgError, setImgError] = useState(false);

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick?.()}
      style={{
        borderRadius: 12, boxShadow: '0 2px 6px rgba(0,0,0,.06)', border: '1px solid #e5e7eb',
        background: '#fff', overflow: 'hidden', cursor: onClick ? 'pointer' : 'default',
        transform: 'translateY(0)', transition: 'transform .18s ease, box-shadow .18s ease',
        position: 'relative',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 22px rgba(0,0,0,.08)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,.06)'; }}
    >
      <div style={{ position: 'relative', width: '100%', height: 160, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {image && !imgError ? (
          <>
            {imgLoading && (
              <div style={imageLoader}>
                <svg width="28" height="28" viewBox="0 0 50 50" aria-hidden>
                  <circle cx="25" cy="25" r="20" stroke="#9ca3af" strokeWidth="4" fill="none" strokeDasharray="31.4 31.4">
                    <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                  </circle>
                </svg>
                <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>Loading…</div>
              </div>
            )}
            <img
              src={image}
              alt={name || 'Wrap'}
              style={{ width: '100%', height: 160, objectFit: 'cover', opacity: imgLoading ? 0 : 1, transition: 'opacity .35s ease', display: 'block' }}
              loading="lazy"
              onLoad={() => setImgLoading(false)}
              onError={() => { setImgError(true); setImgLoading(false); }}
            />
          </>
        ) : (
          <div style={{ color: '#9ca3af', fontSize: 12 }}>No image</div>
        )}

        {/* Customer overlay actions */}
        {isCustomer && (
          <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 8 }}>
            {/* Favorite (heart) */}
            <button
              type="button"
              title={isFav ? 'Remove favorite' : 'Add to favorites'}
              onClick={(e) => { e.stopPropagation(); onAskFavorite?.(); }}
              style={{
                ...chipBtn,
                background: isFav ? '#fef3f2' : '#ffffff',
                borderColor: isFav ? '#fecaca' : '#e5e7eb',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill={isFav ? '#ef4444' : 'none'} stroke={isFav ? '#ef4444' : '#374151'} strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </button>

            {/* Claim */}
            <button
              type="button"
              title={isClaimed ? 'Claimed' : 'Claim this wrap'}
              onClick={(e) => { e.stopPropagation(); onAskClaim?.(); }}
              style={{
                ...chipBtn,
                background: isClaimed ? '#eff6ff' : '#ffffff',
                borderColor: isClaimed ? '#bfdbfe' : '#e5e7eb',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isClaimed ? '#2563eb' : '#374151'} strokeWidth="2">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L3 13V3h10l7.59 7.59a2 2 0 0 1 0 2.82z"/>
                <circle cx="7.5" cy="7.5" r="1.5"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      <div style={{ padding: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{name || 'Untitled wrap'}</div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>{brand || ''}</div>
      </div>
    </div>
  );
};

// ---------- Page ----------
export default function DashboardPage() {
  const navigate = useNavigate();

  // detect customer role
  const isCustomer = useMemo(() => {
    const r = (localStorage.getItem('wrapmarket_role') || '').toLowerCase();
    const roleField = (localStorage.getItem('role') || '').toLowerCase();
    return r === 'customer' || roleField === 'customer';
  }, []);

  // data + fetch
  const [wraps, setWraps] = useState([]);
  const [loading, setLoading] = useState(true);

  // favorites & claims (slug arrays)
  const [favorites, setFavorites] = useState(() => new Set(LS.getJSON('wrap_favorites', [])));
  const [claims, setClaims] = useState(() => new Set(LS.getJSON('wrap_claims', [])));

  // modals
  const [confirmFav, setConfirmFav] = useState({ open: false, slug: '', name: '' });
  const [confirmClaim, setConfirmClaim] = useState({ open: false, slug: '', name: '' });

  // page animation
  const [entered, setEntered] = useState(false);

  // search & filters
  const [q, setQ] = useState('');
  const dq = useDebouncedValue(q, 250);
  const [brandFilter, setBrandFilter] = useState('ALL');
  const [hasImage, setHasImage] = useState(false);
  const [sortKey, setSortKey] = useState('name-asc');

  // lazy-load paging
  const PAGE_SIZE = 200;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(false); // throttle growth

  // user menu (right side)
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const avatarBtnRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (
        menuOpen &&
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        avatarBtnRef.current &&
        !avatarBtnRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  // fetch data
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch((process.env.PUBLIC_URL || '') + '/wraps.json', { cache: 'no-store' });
        const json = await res.json();
        if (mounted) setWraps(Array.isArray(json) ? json : (Array.isArray(json?.data) ? json.data : []));
      } catch {
        if (mounted) setWraps([]);
      } finally {
        if (mounted) {
          setLoading(false);
          requestAnimationFrame(() => setEntered(true));
        }
      }
    })();
    return () => { mounted = false; };
  }, []);

  // ---------- stable slug (no idx) ----------
  const toSlug = (item) => {
    const name  = (item?.name  || '').trim();
    const brand = (item?.brand || '').trim();
    const image = (item?.image || '').trim();
    const key =
      (brand && name && `${brand}-${name}`) ||
      name ||
      brand ||
      image ||
      JSON.stringify(item || {});
    return slugify(key);
  };

  // ---------- migrate legacy slugs to stable ones (favorites & claims) ----------
  useEffect(() => {
    if (!wraps.length) return;

    let changedFav = false;
    let changedClaim = false;

    const nextFavs = new Set(favorites);
    const nextClaims = new Set(claims);

    wraps.forEach((w, i) => {
      const legacy = slugify(w.name || `wrap-${i}`); // what old code used
      const stable = toSlug(w);

      if (nextFavs.has(legacy) && !nextFavs.has(stable)) {
        nextFavs.delete(legacy);
        nextFavs.add(stable);
        changedFav = true;
      }
      if (nextClaims.has(legacy) && !nextClaims.has(stable)) {
        nextClaims.delete(legacy);
        nextClaims.add(stable);
        changedClaim = true;
      }
    });

    if (changedFav) setFavorites(nextFavs);
    if (changedClaim) setClaims(nextClaims);
  }, [wraps]); // run after wraps load

  // persist favorites/claims
  useEffect(() => {
    LS.setJSON('wrap_favorites', Array.from(favorites));
  }, [favorites]);
  useEffect(() => {
    LS.setJSON('wrap_claims', Array.from(claims));
  }, [claims]);

  // options
  const brandOptions = useMemo(() => {
    const set = new Set();
    for (const w of wraps) {
      const b = (w.brand || '').trim();
      if (b) set.add(b);
    }
    return ['ALL', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [wraps]);

  // filter + sort
  const filteredSorted = useMemo(() => {
    const s = dq.trim().toLowerCase();
    let arr = wraps.filter((w) => {
      const name = (w.name || '').toLowerCase();
      const brand = (w.brand || '').toLowerCase();
      const matchesSearch = s.length < 2 ? true : name.includes(s) || brand.includes(s);
      const matchesBrand = brandFilter === 'ALL' ? true : (w.brand || '') === brandFilter;
      const matchesImg = hasImage ? Boolean((w.image || '').trim()) : true;
      return matchesSearch && matchesBrand && matchesImg;
    });

    switch (sortKey) {
      case 'name-asc':   arr.sort((a, b) => (a.name || '').localeCompare(b.name || '')); break;
      case 'name-desc':  arr.sort((a, b) => (b.name || '').localeCompare(a.name || '')); break;
      case 'brand-asc':  arr.sort((a, b) => (a.brand || '').localeCompare(b.brand || '') || (a.name || '').localeCompare(b.name || '')); break;
      case 'brand-desc': arr.sort((a, b) => (b.brand || '').localeCompare(a.brand || '') || (a.name || '').localeCompare(b.name || '')); break;
      default: break;
    }
    return arr;
  }, [wraps, dq, brandFilter, hasImage, sortKey]);

  // reset paging when the result set changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [dq, brandFilter, hasImage, sortKey, wraps]);

  // intersection observer to grow visibleCount
  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const obs = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && !loadingMoreRef.current) {
        loadingMoreRef.current = true;
        setTimeout(() => {
          setVisibleCount((v) => Math.min(v + PAGE_SIZE, filteredSorted.length));
          loadingMoreRef.current = false;
        }, 120);
      }
    }, { rootMargin: '1200px 0px 1200px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [filteredSorted.length]);

  // slice for current page
  const pageItems = useMemo(
    () => filteredSorted.slice(0, visibleCount),
    [filteredSorted, visibleCount]
  );

  // actions
  const isFavSlug = (slug) => favorites.has(slug);
  const isClaimSlug = (slug) => claims.has(slug);

  const askFavorite = (slug, name) => setConfirmFav({ open: true, slug, name });
  const askClaim = (slug, name) => setConfirmClaim({ open: true, slug, name });

  const confirmFavorite = () => {
    const { slug } = confirmFav;
    if (!slug) return setConfirmFav({ open: false, slug: '', name: '' });
    const next = new Set(favorites);
    next.has(slug) ? next.delete(slug) : next.add(slug); // toggle
    setFavorites(next);
    setConfirmFav({ open: false, slug: '', name: '' });
  };

  // Renamed to avoid collision with state variable `confirmClaim`
  const confirmClaimAction = () => {
    const { slug } = confirmClaim;
    if (!slug) return setConfirmClaim({ open: false, slug: '', name: '' });
    const next = new Set(claims);
    next.add(slug); // claiming is one-way (idempotent)
    setClaims(next);
    setConfirmClaim({ open: false, slug: '', name: '' });
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      localStorage.removeItem('role');
      localStorage.removeItem('userId');
      // keep favorites/claims for demo; remove if you want to clear:
      // localStorage.removeItem('wrap_favorites');
      // localStorage.removeItem('wrap_claims');
    } catch {}
    navigate('/login');
  };
  const handleProfile = () => navigate('/profile', { replace: false });

  return (
    <div id="dashboard-page" style={pageWrap}>
      {/* Top bar */}
      <div style={topBar}>
        <img src={logo} alt="The Wrap Market" style={wordmark} onClick={() => navigate('/dashboard')} />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, position: 'relative' }}>
          {/* Customer quick links (passing state) */}
          {isCustomer && (
            <>
              <button
                onClick={() => navigate('/favorites', { state: { favorites: Array.from(favorites) } })}
                style={topLinkBtn}
                title="View favorites"
              >
                <span style={{ marginRight: 6 }}>❤️</span> Favorites
              </button>
              <button
                onClick={() => navigate('/claims', { state: { claims: Array.from(claims) } })}
                style={topLinkBtn}
                title="View claimed wraps"
              >
                <span style={{ marginRight: 6 }}>🏷️</span> Claimed
              </button>
            </>
          )}

          <button
            ref={avatarBtnRef}
            onClick={() => setMenuOpen((s) => !s)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}
            title="Account"
          >
            <Avatar size={30} />
          </button>
          {menuOpen && (
            <div ref={menuRef} role="menu" style={menu}>
              <div style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ fontSize: 12, color: '#6b7280' }}>Signed in as</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{localStorage.getItem('username') || 'guest'}</div>
              </div>
              <button onClick={() => { setMenuOpen(false); handleProfile(); }} role="menuitem" style={menuItem}>Profile</button>
              <button onClick={() => { setMenuOpen(false); handleLogout(); }} role="menuitem" style={{ ...menuItem, color: '#dc2626' }}>Logout</button>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div style={{ padding: 16 }}>
        <div className="controls-card" style={controlsCard}>
          <div className="controls-grid" style={controlsGrid}>
            {/* Search */}
            <div className="controls-item controls-search" style={{ position: 'relative' }}>
              <label className="controls-label">Search</label>
              <div className="controls-search-inner" style={searchInner}>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search wraps (name or brand)…"
                  className="controls-input"
                  style={searchInput}
                />
                <div className="controls-icon-left" style={searchIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="11" cy="11" r="7" stroke="#9ca3af" strokeWidth="2" />
                    <path d="M20 20l-3.5-3.5" stroke="#9ca3af" strokeWidth="2" />
                  </svg>
                </div>
                {q && (
                  <button onClick={() => setQ('')} aria-label="Clear search" className="controls-clear-btn" style={clearBtn}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M6 6l12 12M18 6L6 18" stroke="#9ca3af" strokeWidth="2" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Brand */}
            <div className="controls-item">
              <label className="controls-label">Brand</label>
              <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} className="controls-select" style={select}>
                {brandOptions.map((b) => (<option key={b} value={b}>{b}</option>))}
              </select>
            </div>

            {/* Has image */}
            <div className="controls-item">
              <label className="controls-label">Filter</label>
              <div className="controls-box" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input id="hasImage" type="checkbox" checked={hasImage} onChange={(e) => setHasImage(e.target.checked)} style={{ cursor: 'pointer' }} />
                <label htmlFor="hasImage" style={{ fontSize: 13, color: '#374151', cursor: 'pointer' }}>Has image</label>
              </div>
            </div>

            {/* Sort */}
            <div className="controls-item">
              <label className="controls-label">Sort</label>
              <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} className="controls-select" style={select}>
                <option value="name-asc">Name (A → Z)</option>
                <option value="name-desc">Name (Z → A)</option>
                <option value="brand-asc">Brand (A → Z)</option>
                <option value="brand-desc">Brand (Z → A)</option>
              </select>
            </div>
          </div>

          {/* helper line */}
          <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
            {dq.trim().length < 2 && q.length > 0
              ? <>Keep typing — search runs after <strong>2+ letters</strong>.</>
              : <>Showing <strong style={{ color: '#111827' }}>{pageItems.length}</strong> of <strong style={{ color: '#111827' }}>{filteredSorted.length}</strong> result(s){brandFilter !== 'ALL' ? <> · Brand: <strong style={{ color: '#111827' }}>{brandFilter}</strong></> : null}{hasImage ? ' · Has image' : ''}</>}
          </div>
        </div>
      </div>

      {/* Results */}
      <div style={{ padding: 16, paddingTop: 0 }}>
        {loading ? (
          <div style={{ color: '#6b7280', fontSize: 14 }}>Loading sample wraps…</div>
        ) : filteredSorted.length === 0 ? (
          <div style={{ color: '#6b7280', fontSize: 14 }}>No results.</div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 16,
                opacity: entered ? 1 : 0,
                transform: entered ? 'translateY(0)' : 'translateY(6px)',
                transition: 'opacity .28s ease, transform .28s ease',
              }}
              className="grid-responsive"
            >
              {pageItems.map((item, idx) => {
                const slug = toSlug(item); // ✅ stable slug (no idx)
                const fav = isFavSlug(slug);
                const claimed = isClaimSlug(slug);

                return (
                  <Card
                    key={`${item.name}-${idx}`}
                    item={item}
                    isCustomer={isCustomer}
                    isFav={fav}
                    isClaimed={claimed}
                    onAskFavorite={() => askFavorite(slug, item.name || `wrap-${idx}`)}
                    onAskClaim={() => askClaim(slug, item.name || `wrap-${idx}`)}
                    onClick={() => navigate(`/wrap/${slug}`, { state: { item } })}
                  />
                );
              })}
            </div>

            {/* Sentinel for infinite scroll */}
            <div ref={sentinelRef} style={{ height: 1 }} />

            {/* Load more / end indicator */}
            <div style={{ textAlign: 'center', marginTop: 12, color: '#6b7280', fontSize: 12 }}>
              {pageItems.length < filteredSorted.length ? (
                <button
                  onClick={() => setVisibleCount((v) => Math.min(v + PAGE_SIZE, filteredSorted.length))}
                  style={{
                    marginTop: 6, padding: '8px 14px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff',
                    cursor: 'pointer', fontWeight: 600, fontSize: 12
                  }}
                >
                  Load more ({filteredSorted.length - pageItems.length} left)
                </button>
              ) : (
                <span>End of results</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Favorite modal */}
      <Modal
        open={confirmFav.open}
        title={favorites.has(confirmFav.slug) ? 'Remove from favorites?' : 'Add to favorites?'}
        body={
          favorites.has(confirmFav.slug)
            ? `Do you want to remove “${confirmFav.name}” from your favorites?`
            : `Do you want to add “${confirmFav.name}” to your favorites?`
        }
        confirmLabel="Yes"
        onConfirm={confirmFavorite}
        onClose={() => setConfirmFav({ open: false, slug: '', name: '' })}
      />

      {/* Claim modal */}
      <Modal
        open={confirmClaim.open}
        title="Claim this wrap?"
        body={`Are you sure you want to claim “${confirmClaim.name}”?`}
        confirmLabel="Yes, claim"
        onConfirm={confirmClaimAction}
        onClose={() => setConfirmClaim({ open: false, slug: '', name: '' })}
      />
    </div>
  );
}

/* ---------- styles ---------- */
const pageWrap = { minHeight: '100vh', background: '#f5f7fb', maxWidth: '100vw', overflowX: 'hidden', boxSizing: 'border-box' };
const topBar = {
  height: 56, background: '#fff', borderBottom: '1px solid #e5e7eb',
  display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px',
  position: 'sticky', top: 0, zIndex: 20, maxWidth: '100vw', boxSizing: 'border-box',
};
const wordmark = { height: 34, width: 'auto', cursor: 'pointer', filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.25))' };
const menu = {
  position: 'absolute', top: 46, right: 0, width: 220, background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 12, boxShadow: '0 14px 32px rgba(0,0,0,.12)', overflow: 'hidden', zIndex: 30
};
const menuItem = { width: '100%', textAlign: 'left', padding: '10px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14 };
const topLinkBtn = {
  border: '1px solid #e5e7eb', background: '#fff', padding: '6px 10px', borderRadius: 999,
  cursor: 'pointer', fontSize: 12, color: '#111827', display: 'inline-flex', alignItems: 'center'
};

const controlsCard = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 6px 18px rgba(0,0,0,.05)', padding: 12 };
const controlsGrid = { display: 'grid', gridTemplateColumns: '1fr 220px 160px 160px', gap: 12 };
const searchInner = { position: 'relative', display: 'flex', alignItems: 'center' };
const searchInput = { width: '100%', padding: '8px 32px 8px 30px', borderRadius: 10, border: '1px solid #e5e7eb', outline: 'none', fontSize: 14 };
const searchIcon = { position: 'absolute', left: 8, pointerEvents: 'none' };
const clearBtn = { position: 'absolute', right: 8, display: 'grid', placeItems: 'center', border: 'none', background: 'transparent', cursor: 'pointer' };
const select = { width: '100%', padding: '8px 10px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 14 };

const imageLoader = {
  position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
  background: 'rgba(249,250,251,.6)', backdropFilter: 'blur(2px)',
};
const chipBtn = {
  border: '1px solid #e5e7eb', borderRadius: 999, padding: '6px 8px', background: '#fff', cursor: 'pointer'
};
const btnPrimary = {
  background: '#1E2A38', color: '#fff', padding: '8px 12px', borderRadius: 10, fontWeight: 700,
  border: 'none', cursor: 'pointer'
};
const btnGhost = {
  background: '#fff', color: '#111827', padding: '8px 12px', borderRadius: 10,
  border: '1px solid #e5e7eb', cursor: 'pointer'
};

// Modal styles
const modalBack = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)',
  display: 'grid', placeItems: 'center', zIndex: 1000
};
const modalCard = {
  background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb',
  padding: 16, width: 'min(90vw, 420px)', boxShadow: '0 18px 44px rgba(0,0,0,.18)'
};

/* ----- responsive CSS injected once ----- */
const css = `
#dashboard-page { max-width: 100vw; overflow-x: hidden; }
@media (max-width: 1024px) {
  .controls-grid { grid-template-columns: 1fr 200px 140px 140px !important; }
}
@media (max-width: 860px) {
  .controls-grid { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 520px) {
  .controls-grid { grid-template-columns: 1fr; }
}
`;
const styleId = 'dashboard-inline-style';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const tag = document.createElement('style');
  tag.id = styleId;
  tag.innerHTML = css;
  document.head.appendChild(tag);
}
